import chroma from 'chroma-js';
import { findIndex, isNumber, isEmpty, clone, isUndefined, find, isFinite } from 'lodash';

const { hasOwnProperty } = Object.prototype;

const numericValue = candidate => {
    const parsed = parseFloat(candidate);
    return Number.isNaN(parsed) ? null : parsed;
};

const pickRangeFormatValue = (values, index) => values[Math.min(index, values.length - 1)];

const isRangeFormatConfigValid = (ranges, values, value) =>
    Array.isArray(ranges) && Array.isArray(values) && !Number.isNaN(value);

const normalizeRangeLimits = ranges => {
    const normalized = [];
    for (const limit of ranges) {
        const parsedLimit = numericValue(limit);
        if (parsedLimit === null) {
            return null;
        }
        normalized.push(parsedLimit);
    }
    return normalized;
};

const DEFAULT_GRADIENT_COLORS = ['white', 'red'];

export const NON_NUMERIC_RANGE_ERROR =
    'Could not format value: Ranges are required to be numbers. Falling back to first color';
export const VALUES_AND_RANGES_HAVE_DIFFERENT_LENGTH =
    'gradient ranges and values must have the same number of elements';
export const INVALID_VALUE = 'Could not format value for gradient:';

const ensureColorValues = values => {
    if (!values || values.length === 0) {
        return [...DEFAULT_GRADIENT_COLORS];
    }
    if (values.length === 1) {
        return ['white', values[0]];
    }
    return [...values];
};

const expandColorRanges = (ranges, colorValues, data = []) => {
    if (ranges && ranges.length) {
        return [...ranges];
    }

    if (Array.isArray(data) && data.length > 0) {
        const min = Math.min(...data);
        const max = Math.max(...data);
        const computed = [min];
        const steps = colorValues.length - 1;

        for (let i = 1; i < steps; i += 1) {
            computed.push(min + (i * (max - min)) / steps);
        }
        computed.push(max);
        return computed;
    }

    return [0, 1];
};

const sortRangesWithFallback = colorRanges => {
    if (colorRanges.length === 1) {
        return [...colorRanges, colorRanges[0]];
    }
    return [...colorRanges].sort((a, b) => parseFloat(a) - parseFloat(b));
};

const getGradientBoundaryColor = (floatVal, colorRanges, colorValues) => {
    const lowest = numericValue(colorRanges[0]);
    const highest = numericValue(colorRanges[colorRanges.length - 1]);

    if (highest === null || lowest === null) {
        return null;
    }

    if (floatVal > highest) {
        return chroma(colorValues[colorValues.length - 1])
            .hex()
            .toUpperCase();
    }
    if (floatVal < lowest) {
        return chroma(colorValues[0]).hex().toUpperCase();
    }
    return null;
};

const getGradientErrorColor = colorValues =>
    colorValues.length ? chroma(colorValues[0]).hex().toUpperCase() : '#000';

const interpolateGradientColor = (floatVal, colorRanges, colorValues, errorHandler) => {
    for (let i = 1; i < colorRanges.length; i += 1) {
        const upperVal = numericValue(colorRanges[i]);
        if (upperVal === null) {
            errorHandler(NON_NUMERIC_RANGE_ERROR);
            return null;
        }

        if (floatVal <= upperVal) {
            const lowerVal = numericValue(colorRanges[i - 1]);
            if (lowerVal === null) {
                errorHandler(NON_NUMERIC_RANGE_ERROR);
                return null;
            }

            let tau = (floatVal - lowerVal) / (upperVal - lowerVal);
            if (upperVal === lowerVal) {
                tau = floatVal === 0 ? 0 : 1;
            }

            const upperColor = colorValues[i];
            const lowerColor = colorValues[i - 1];
            return chroma.scale([lowerColor, upperColor])(tau).hex().toUpperCase();
        }
    }
    return null;
};

const getCorrectedIndex = (length, index) => {
    if (!isNumber(index)) {
        return null;
    }
    const isNegative = index < 0;
    const absoluteIndex = Math.abs(index);

    if ((isNegative && absoluteIndex > length) || (!isNegative && index > length - 1)) {
        return null;
    }

    return isNegative ? length - absoluteIndex : index;
};

const getColumnData = (columnValuesInput, columnIndex) => {
    const columnValues = columnValuesInput || [];
    if (!isNumber(columnIndex)) {
        return columnValues;
    }

    const correctedIndex = getCorrectedIndex(columnValues.length, columnIndex);
    if (correctedIndex === null) {
        return null;
    }

    const valueAtIndex = columnValues[correctedIndex];
    return columnValues.map(() => valueAtIndex);
};

const getFieldNameFromEntry = entry => (typeof entry === 'object' ? entry.name : entry);

const getFieldDataAtIndex = (data, fieldIndex, columnIndex) => {
    const { fields = [], columns = [] } = data;
    const correctedFieldIndex = getCorrectedIndex(fields.length, fieldIndex);
    if (correctedFieldIndex === null) {
        return {};
    }

    const fieldName = getFieldNameFromEntry(fields[correctedFieldIndex]);
    const columnValues = columns[correctedFieldIndex];
    if (!columnValues) {
        return {};
    }

    const fieldData = getColumnData(columnValues, columnIndex);
    if (!fieldData) {
        return {};
    }

    return {
        fieldName,
        data: fieldData,
    };
};

const isWordToken = value => /^\w+$/.test(value);

const inspectReference = fieldReferenceStr => {
    if (typeof fieldReferenceStr !== 'string') {
        return null;
    }

    const firstBracket = fieldReferenceStr.indexOf('[');
    const firstDot = fieldReferenceStr.indexOf('.');
    const hasIndexReference = firstBracket !== -1;
    const hasNameReference = firstDot !== -1 && (firstBracket === -1 || firstDot < firstBracket);

    if (!hasIndexReference && !hasNameReference) {
        return null;
    }

    const cutoffCandidates = [];
    if (hasIndexReference) {
        cutoffCandidates.push(firstBracket);
    }
    if (hasNameReference) {
        cutoffCandidates.push(firstDot);
    }
    const cutoff = Math.min(...cutoffCandidates);
    const dataSourceType = fieldReferenceStr.slice(0, cutoff);
    if (!isWordToken(dataSourceType)) {
        return null;
    }
    return {
        isIndexBasedReference: hasIndexReference && firstBracket === cutoff,
        isNameBasedReference: hasNameReference,
        dataSourceType,
    };
};

function buildFieldRangeDataForReference(data, parsedReference) {
    const { isFieldIndexRange, fromFieldIndex, toFieldIndex, columnIndex } = parsedReference;
    if (!isFieldIndexRange || fromFieldIndex <= -1) {
        return null;
    }

    const { fields } = data;
    const endIndex =
        toFieldIndex > -1 ? Math.min(fields.length - 1, toFieldIndex) : fields.length + toFieldIndex;
    const dataForRange = [];
    for (let i = fromFieldIndex; i <= endIndex; i += 1) {
        dataForRange.push(getFieldDataAtIndex(data, i, columnIndex));
    }
    return dataForRange;
}

const buildFieldReferenceMetadata = fieldReferenceStr => inspectReference(fieldReferenceStr);

const getFieldReferenceString = fieldReference => {
    if (typeof fieldReference === 'string') {
        return fieldReference;
    }
    if (!fieldReference || typeof fieldReference !== 'object') {
        return fieldReference;
    }
    if (Array.isArray(fieldReference.field) && fieldReference.field.length) {
        return fieldReference.field[fieldReference.field.length - 1];
    }
    return fieldReference.field;
};

/**
 * getDataTypeForPoint
 * naive implementation of checking for the data type of a single data point
 * number > time > string > unknown
 * starting with number because a Date.parse(number) is a valid date
 *
 * @param {any} dataPoint
 * @return {string} type
 */
export const getDataTypeForPoint = dataPoint => {
    if (isFinite(+dataPoint) && isNumber(+dataPoint)) {
        return 'number';
    }
    if (!Number.isNaN(Date.parse(dataPoint))) {
        return 'time';
    }
    if (typeof dataPoint === 'string') {
        return 'string';
    }
    // nulls, objects, etc
    return 'unknown';
};

/**
 * inferDataTypeFromSample
 * based on a number of data points this function determines the data type for all of them
 * and returns the most common data type (naive type inference)
 *
 * @param {Array} dataSample an array of arbitrary size containing data points of any data type
 * @return {string} type the most common type in the sample
 */
export const inferDataTypeFromSample = dataSample => {
    const typeMatches = {
        time: 0,
        number: 0,
        string: 0,
        unknown: 0,
    };
    dataSample.forEach(point => {
        typeMatches[getDataTypeForPoint(point)] += 1;
    });
    let typeCount = -1;
    let returnType = 'unknown';
    Object.keys(typeMatches).forEach(key => {
        if (typeMatches[key] > typeCount) {
            typeCount = typeMatches[key];
            returnType = key;
        }
    });
    return returnType;
};

/**
 * based on a data array (e.g. one data column) this function returns a tiny sample of it
 *
 * @param {Array} data to draw a sample from
 * @return {Array} a sample of the passed data
 */
export const drawSample = data => {
    if (data.length > 2) {
        return [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]];
    }
    if (data.length === 2) {
        return [data[0], data[1]];
    }
    // small enough to check everything
    return data;
};

function resolveDefaultFieldReference(dataSources, fieldReference) {
    if (!fieldReference || !fieldReference.isDefault || !Array.isArray(fieldReference.field)) {
        return null;
    }

    for (const candidate of fieldReference.field) {
        const dataFromDefault = getFieldDataFromReferenceString(dataSources, candidate);
        if (dataFromDefault && Array.isArray(dataFromDefault.data)) {
            const type = inferDataTypeFromSample(drawSample(dataFromDefault.data));
            if (!fieldReference.type || fieldReference.type.includes(type)) {
                return dataFromDefault;
            }
        }
    }

    return null;
}

export const rangeFormat = (value, { ranges, values } = {}) => {
    if (!isRangeFormatConfigValid(ranges, values, value)) {
        return value;
    }

    const normalizedRanges = normalizeRangeLimits(ranges);
    if (!normalizedRanges) {
        return value;
    }

    const floatVal = parseFloat(value);
    const matchedIndex = normalizedRanges.findIndex(limit => floatVal <= limit);
    if (matchedIndex > -1) {
        const formattedValue = pickRangeFormatValue(values, matchedIndex);
        return formattedValue ?? value;
    }

    const lastRange = normalizedRanges[normalizedRanges.length - 1];
    if (floatVal > lastRange) {
        return values[values.length - 1] ?? value;
    }

    return value;
};

export const categoryFormat = (value, { categories, values }) => {
    if (!(Array.isArray(categories) && Array.isArray(values) && categories.length === values.length)) {
        // Could not format value: categories & values should be arrays of the same length. Falling back to original value
        return value;
    }
    const valueIndex = categories.indexOf(value);
    if (valueIndex > -1) {
        return values[valueIndex];
    }
    // Could not format value: Category does not exist. Falling back to original value
    return value;
};

/**
 * based on a range configuration this formatter maps the value to a value specified in a range.
 * the range fitting follows this criteria: `range.from <= value < range.to` so the `to` value is not included
 * A range can be defined as either a closed bound range: `{ from: 10, to: 20, value: 'foo' }`
 * or an open bound range:
 * `{ to: 20, value: 'bar' }` (open lower bound)
 * `{ from: 100, value: 'oof' }` (open upper bound)
 *
 * @param {string} value from data
 * @param {object} formatConfig
 * @param {Object[]} formatConfig.ranges
 */
export const rangeValueFormat = (value, { ranges = [] }) => {
    const floatValue = parseFloat(value);
    if (Number.isNaN(Number(value))) {
        return value;
    }
    for (let i = 0; i < ranges.length; i += 1) {
        if (
            // open upper bound: value is bigger than or equal to open upper bound start (from)
            (i === 0 && hasOwnProperty.call(ranges[i], 'from') && floatValue >= ranges[i].from) ||
            // inbetween: value falls into from - to range
            (floatValue >= ranges[i].from && floatValue < ranges[i].to) ||
            // open lower bound: value is smaller than or equal to open lower bound end (to)
            (i === ranges.length - 1 && hasOwnProperty.call(ranges[i], 'to') && floatValue < ranges[i].to)
        ) {
            return ranges[i].value;
        }
    }
    return value;
};

/**
 * this formatter maps the value to a value specified in a list of potential matches .
 * if the value passed is a match for what is provided in `matches[i].match`, we return that mapped value `matches[i].value`.
 * If there is not match in the array and there is a `defaultValue` provided we return that value. If there is not a match in the list
 * and no defaultValue is passed, we return the original `value`
 * `{ match: 'cherry', value: 'red' }` (exact match)
 *
 * @param {string} value from data
 * @param {object} formatConfig
 * @param {Object[]} formatConfig.matches
 * @param {object} formatConfig.defaultValue
 */
export const matchValueFormat = (value, { matches = [], defaultValue = undefined }) => {
    const defaultResult = isUndefined(defaultValue) ? value : defaultValue;
    const matchResult = find(matches, match => match.match && match.match === value);

    return isUndefined(matchResult) ? defaultResult : matchResult.value;
};

/**
 * based on ranges and values this formatter maps the value to an interpolated color
 *
 * @param {string} value from data (string representation of number)
 * @param {object} formatConfig
 * @param {number[]} formatConfig.ranges value ranges
 * @param {string[]} formatConfig.values color stops to interpolate from
 * @param {string[]} data the data to be formatted
 * @param errorHandler function
 */
export const colorGradientFormat = (value, { ranges, values }, data, errorHandler) => {
    const colorValues = ensureColorValues(values);
    const errorColor = () => getGradientErrorColor(colorValues);

    if (Number.isNaN(Number(value))) {
        errorHandler(`${INVALID_VALUE} ${value}`);
        return errorColor();
    }

    const expandedRanges = expandColorRanges(ranges, colorValues, data);
    const colorRanges = sortRangesWithFallback(expandedRanges);

    if (colorRanges.length !== colorValues.length) {
        errorHandler(VALUES_AND_RANGES_HAVE_DIFFERENT_LENGTH);
        return errorColor();
    }

    const floatVal = parseFloat(value);
    const boundaryColor = getGradientBoundaryColor(floatVal, colorRanges, colorValues);
    if (boundaryColor) {
        return boundaryColor;
    }

    let handledError = false;
    const trackedErrorHandler = msg => {
        handledError = true;
        errorHandler(msg);
    };

    const interpolatedColor = interpolateGradientColor(
        floatVal,
        colorRanges,
        colorValues,
        trackedErrorHandler
    );
    if (interpolatedColor) {
        return interpolatedColor;
    }

    if (handledError) {
        return errorColor();
    }

    errorHandler(`${INVALID_VALUE} ${value}`);
    return errorColor();
};

const formatters = {
    rangevalue: rangeValueFormat,
    matchvalue: matchValueFormat,
    gradient: colorGradientFormat,
};

export const MAX_LOGGED_FORMATTER_ERRORS = 10;
export const newFormatterErrorHandler = (maxErrors, handlerFunc) => {
    let errorCount = 0;
    return msg => {
        if (errorCount < maxErrors) {
            handlerFunc(msg);
        }
        errorCount += 1;
    };
};

/**
 * returns a formatter function based on a format config
 * @param {Object} formatCfg formatter config
 * @param {number[]} formatCfg.ranges an array of numeric ranges that will be used to map to values. requires formatCfg.values
 * @param {*[]} formatCfg.values  an array of values to map to. can be used with formatCfg.ranges or formatCfg.categories or formatCfg.gradient
 * @param {string[]|number[]} formatCfg.categories  an array of categories. categories are used for a 1:1 mapping to values. requires formatCfg.values
 * @param {number[]} data the data to be formatted
 */
export const getFormatter = (data, formatCfg = {}) => {
    if (formatCfg.type) {
        if (formatters[formatCfg.type]) {
            return value =>
                formatters[formatCfg.type](
                    value,
                    formatCfg,
                    data,
                    // eslint-disable-next-line no-console
                    newFormatterErrorHandler(MAX_LOGGED_FORMATTER_ERRORS, msg => console.warn(msg))
                );
        }
        return value => value;
    }
    const { ranges, values } = formatCfg;
    if (values) {
        if (ranges) {
            return value => rangeFormat(value, formatCfg);
        }

        if (formatCfg.categories) {
            return value => categoryFormat(value, formatCfg);
        }
    }
    // eslint-disable-next-line no-console
    console.warn('Could not find a formatter. Falling back to identity fn');
    return value => value;
};

function applyFormatIfProvided(format, fieldData) {
    if (!format || !fieldData || isEmpty(fieldData) || !Array.isArray(fieldData.data)) {
        return fieldData;
    }

    const formatValue = getFormatter(fieldData.data, format);
    if (!formatValue) {
        return fieldData;
    }

    return {
        fieldName: fieldData.fieldName,
        data: fieldData.data.map(formatValue),
    };
}

const getDataForDataSourceType = (data, dataSourceType) => {
    if (!dataSourceType || !data[dataSourceType] || !data[dataSourceType].data) {
        return {
            fields: [],
            columns: [],
        };
    }
    return data[dataSourceType].data;
};
/*
 * parse fieldIndexRangeStr
 * @param {String} fieldIndexRangeStr   '0:', '0:5', ':5'
 * @returns {Object} info about parsed range if there is any
 */
export const parseFieldRangeReference = fieldIndexRangeStr => {
    if (!fieldIndexRangeStr || fieldIndexRangeStr.indexOf(':') === -1) {
        return {
            isFieldIndexRange: false,
        };
    }
    const [fromFieldIndexStr, toFieldIndexStr] = fieldIndexRangeStr.split(':');
    return {
        isFieldIndexRange: true,
        fromFieldIndex:
            Number.isNaN(Number(fromFieldIndexStr)) || !fromFieldIndexStr ? 0 : Number(fromFieldIndexStr),
        toFieldIndex:
            Number.isNaN(Number(toFieldIndexStr)) || !toFieldIndexStr ? -1 : Number(toFieldIndexStr),
    };
};
/*
 * parse fieldReferenceStr to get field index and column index for user defined encoding. e.g. 'primary[1][123]'
 * @param {String} fieldReferenceStr   'primary[1][123]'
 * @returns {Object} { fieldIndex: 1, columnIndex: 123, isFieldIndexRange: true, fromFieldIndexRange: 0, toFieldIndexRange: 3 }
 */
export const parseIndexBasedFieldReference = fieldReferenceStr => {
    const indices = fieldReferenceStr
        .replace(/^[A-Za-z0-9]+/, '')
        .replace(/\[/g, '')
        .replace(/\]/g, ',')
        .split(',');
    const dataSourceName = fieldReferenceStr.match(/^[A-Za-z0-9]+/)[0];
    const fieldRange = parseFieldRangeReference(indices[0]);
    return {
        dataSourceName,
        fieldIndex: Number.isNaN(Number(indices[0])) || !indices[0] ? null : Number(indices[0]),
        columnIndex: Number.isNaN(Number(indices[1])) || !indices[1] ? null : Number(indices[1]),
        ...fieldRange,
    };
};

/*
 * parse fieldReferenceStr to get field name and column index for user defined encoding. e.g. 'primary.color[123]'
 * @param {String} fieldReferenceStr   'primary.color[123]'
 * @returns {Object} { fieldName: 'color', columnIndex: 123 }
 */
export const parseNameBasedFieldReference = fieldReferenceStr => {
    const dataSourceName = fieldReferenceStr.match(/^[A-Za-z0-9]+/)[0];
    const fieldNameStr = fieldReferenceStr.split('.')[1];
    const splitedStr = fieldNameStr.split('[');
    const fieldName = splitedStr[0];
    const columnIndex = splitedStr[1] ? Number(splitedStr[1].replace(']', '')) : null;

    return { dataSourceName, fieldName, columnIndex };
};

const getDataForIndexBasedFieldReference = (data, fieldReferenceStr) => {
    const parsedReference = parseIndexBasedFieldReference(fieldReferenceStr);

    const rangeResult = buildFieldRangeDataForReference(data, parsedReference);
    if (rangeResult) {
        return rangeResult;
    }

    return getFieldDataAtIndex(data, parsedReference.fieldIndex, parsedReference.columnIndex);
};

const getDataForNameBasedFieldReference = (data, fieldReferenceStr) => {
    const { fields, columns } = data;
    const { fieldName, columnIndex } = parseNameBasedFieldReference(fieldReferenceStr); // <datasource>.<fieldname>[0]

    if (!fieldName) {
        return {};
    }

    const fieldIndex = findIndex(fields, field =>
        typeof field === 'object' ? field.name === fieldName : field === fieldName
    );

    if (fieldIndex === -1) {
        // eslint-disable-next-line no-console
        console.warn(`field '${fieldName}' not available.`);
        return {};
    }

    const columnValues = columns[fieldIndex];
    if (!columnValues) {
        return {};
    }

    const fieldData = getColumnData(columnValues, columnIndex);
    if (!fieldData) {
        return {};
    }

    return {
        fieldName,
        data: fieldData,
    };
};

function getFieldDataFromReferenceString(dataSources, fieldReferenceStr) {
    if (!fieldReferenceStr) {
        return {};
    }

    const metadata = buildFieldReferenceMetadata(fieldReferenceStr);
    if (!metadata) {
        return {};
    }

    const data = getDataForDataSourceType(dataSources, metadata.dataSourceType);
    return metadata.isIndexBasedReference
        ? getDataForIndexBasedFieldReference(data, fieldReferenceStr)
        : getDataForNameBasedFieldReference(data, fieldReferenceStr);
}

const getDataForFieldReference = (dataSources, fieldReference) => {
    if (Array.isArray(fieldReference) && !fieldReference.isDefault) {
        return fieldReference.map(ref => getDataForFieldReference(dataSources, ref));
    }

    const defaultMatch = resolveDefaultFieldReference(dataSources, fieldReference);
    if (defaultMatch) {
        return defaultMatch;
    }

    const fieldReferenceStr = getFieldReferenceString(fieldReference);
    const fieldData = getFieldDataFromReferenceString(dataSources, fieldReferenceStr);

    if (typeof fieldReference === 'object' && fieldReference.format) {
        return applyFormatIfProvided(fieldReference.format, fieldData);
    }

    return fieldData;
};

export const mergeEncoding = (defaultEncoding, userDefinedEncoding = {}) => {
    const mergedEncoding = { ...userDefinedEncoding };
    Object.keys(defaultEncoding).forEach(key => {
        if (
            isEmpty(userDefinedEncoding[key]) &&
            defaultEncoding[key].isRequired &&
            defaultEncoding[key].default
        ) {
            mergedEncoding[key] =
                typeof defaultEncoding[key].default === 'object' &&
                !Array.isArray(defaultEncoding[key].default)
                    ? {
                          ...defaultEncoding[key].default,
                          isDefault: true,
                          type: defaultEncoding[key].type,
                      }
                    : {
                          field: defaultEncoding[key].default,
                          isDefault: true,
                          type: defaultEncoding[key].type,
                      };
        }
    });
    return mergedEncoding;
};

export const parse = (dataSources, encoding = {}) => {
    const parsedOutput = {
        _meta: {
            fieldNames: {},
            types: {},
        },
    };

    Object.keys(encoding).forEach(key => {
        let fieldReference = clone(encoding[key]);

        // handle the case for encoding field reference, for example fill: 'encoding.trend'
        if (
            typeof fieldReference === 'object' &&
            fieldReference.field &&
            typeof fieldReference.field === 'string' &&
            fieldReference.field.startsWith('encoding')
        ) {
            const replacedKey = fieldReference.field.split('.')[1];

            // return for the below invalid cases
            // `encoding.z` if `z` is not a valid field in encoding
            if (!encoding[replacedKey]) {
                // eslint-disable-next-line no-console
                console.warn(`${key} field cannot refer to 'encoding.${replacedKey}'.`);
            }
            // if `z` is an object configuration in encoding simply extract the field when refering `encoding.z`
            const field =
                typeof encoding[replacedKey] === 'object'
                    ? encoding[replacedKey].field
                    : encoding[replacedKey];
            fieldReference = {
                ...fieldReference,
                field,
            };
        }

        const dataForFieldReference = getDataForFieldReference(dataSources, fieldReference);

        if (Array.isArray(dataForFieldReference)) {
            parsedOutput[key] = dataForFieldReference.map(entry => entry.data);
            parsedOutput._meta.fieldNames[key] = dataForFieldReference.map(entry => entry.fieldName); // eslint-disable-line no-underscore-dangle
            // eslint-disable-next-line no-underscore-dangle
            parsedOutput._meta.types[key] = dataForFieldReference.map(entry =>
                inferDataTypeFromSample(drawSample(entry.data))
            );
        } else {
            const { fieldName, data } = dataForFieldReference;
            if (fieldName) {
                parsedOutput[key] = data;
                parsedOutput._meta.fieldNames[key] = fieldName; // eslint-disable-line no-underscore-dangle
                // eslint-disable-next-line no-underscore-dangle
                parsedOutput._meta.types[key] = inferDataTypeFromSample(drawSample(data));
            }
        }
    });

    return parsedOutput;
};
